import { useQueryClient } from '@tanstack/vue-query'
import { toast } from 'vue-sonner'
import { useAuthStore } from '~/stores/authStore'
import { useAssetStore } from '~/stores/assetStore'
import { useMapStore } from '~/stores/mapStore'
import type { Site } from '~/types/locations'

interface UseActiveSiteReturn {
  switchSite: (site?: Site) => void
  resetActiveSite: () => void
  resetIfUnavailable: (sites: Site[]) => boolean
}

export const useActiveSite = (): UseActiveSiteReturn => {
  const nuxtApp = useNuxtApp()
  const authStore = useAuthStore()
  const assetStore = useAssetStore()
  const mapStore = useMapStore()
  const queryClient = useQueryClient()

  const switchSite = (site?: Site): void => {
    if (authStore.activeSite?.id === site?.id) {
      return
    }

    authStore.setActiveSite(site)
    void queryClient.resetQueries()
    assetStore.clearAssetSelection()
    assetStore.clearSelectedAssetIds()
    mapStore.clearPolygons()
  }

  const resetActiveSite = (): void => {
    if (!authStore.activeSite) {
      return
    }

    switchSite(undefined)
    toast(nuxtApp.$i18n.t('siteScope.activeSiteReset'))
  }

  const resetIfUnavailable = (sites: Site[]): boolean => {
    const activeSiteId = authStore.activeSite?.id
    if (activeSiteId === undefined || sites.some((site) => site.id === activeSiteId)) {
      return false
    }

    resetActiveSite()
    return true
  }

  return {
    switchSite,
    resetActiveSite,
    resetIfUnavailable
  }
}
